import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { canAccess, firstPath } from "@/lib/roles";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import LeadPipeline from "@/pages/LeadPipeline";
import RecallCampaigns from "@/pages/RecallCampaigns";
import ReferralAnalytics from "@/pages/ReferralAnalytics";
import CalendarView from "@/pages/CalendarView";
import Procedures from "@/pages/Procedures";
import CommunicationCenter from "@/pages/CommunicationCenter";
import DrChronoSync from "@/pages/DrChronoSync";
import Staff from "@/pages/Staff";
import Messaging from "@/pages/Messaging";
import Medications from "@/pages/Medications";
import Birthdays from "@/pages/Birthdays";
import { Loader2 } from "lucide-react";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );
}

function RoleRoute({ section, children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccess(user.role, section)) return <Navigate to={firstPath(user.role)} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to={firstPath(user.role)} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<RoleRoute section="dashboard"><Dashboard /></RoleRoute>} />
            <Route path="/pipeline" element={<RoleRoute section="pipeline"><LeadPipeline /></RoleRoute>} />
            <Route path="/campaigns" element={<RoleRoute section="campaigns"><RecallCampaigns /></RoleRoute>} />
            <Route path="/referrals" element={<RoleRoute section="referrals"><ReferralAnalytics /></RoleRoute>} />
            <Route path="/calendar" element={<RoleRoute section="calendar"><CalendarView /></RoleRoute>} />
            <Route path="/procedures" element={<RoleRoute section="procedures"><Procedures /></RoleRoute>} />
            <Route path="/communication" element={<RoleRoute section="communication"><CommunicationCenter /></RoleRoute>} />
            <Route path="/drchrono" element={<RoleRoute section="drchrono"><DrChronoSync /></RoleRoute>} />
            <Route path="/staff" element={<RoleRoute section="staff"><Staff /></RoleRoute>} />
            <Route path="/birthdays" element={<RoleRoute section="birthdays"><Birthdays /></RoleRoute>} />
            <Route path="/medications" element={<RoleRoute section="medications"><Medications /></RoleRoute>} />
            <Route path="/messaging" element={<RoleRoute section="messaging"><Messaging /></RoleRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
